import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  getAsientos,
  crearAsiento,
  actualizarAsiento,
  borrarAsiento,
  buscarCuentas,
} from "@/lib/api";
import { esCuentaDeAsiento } from "@/lib/useModoAsiento";
import { formatCurrency } from "@/lib/utils";
import { notifyDataChanged } from "@/lib/events";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Info,
  Pencil,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

// Convierte lo que el usuario teclea (admite coma o punto decimal) a número.
// Vacío o no numérico => 0.
function parseImporte(v) {
  if (v === null || v === undefined) return 0;
  const s = String(v).trim().replace(/\s/g, "").replace(",", ".");
  if (s === "") return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

const filaVacia = () => ({ codigo_full: "", nombre: "", cargo: "", abono: "" });

export default function AsientosPage() {
  const { ejercicioId } = useParams();
  const navigate = useNavigate();

  const [asientos, setAsientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Formulario de nuevo asiento (o de edición, si editandoId != null — W19.7)
  const [concepto, setConcepto] = useState("");
  const [fecha, setFecha] = useState("");
  const [filas, setFilas] = useState(() => [filaVacia(), filaVacia()]);
  // P15 (Pau oct-2026): asiento SIN movimiento de caja (activación de
  // rentings…) — modifica el balance pero el Estado de Flujos lo neutraliza.
  const [sinCaja, setSinCaja] = useState(false);
  const [editandoId, setEditandoId] = useState(null);

  // Autocompletar de cuentas EXISTENTES del ejercicio (petición Héctor
  // sept-2026: "poder escollir els comptes que ja estan creats… i que se't
  // posi el nom automàticament"). Búsqueda en servidor (prefijo de código o
  // texto del nombre); elegir una rellena código Y nombre. Se puede seguir
  // tecleando un código nuevo — las sugerencias son ayuda, no restricción.
  const [sugerencias, setSugerencias] = useState({ idx: null, items: [] });
  const sugTimer = useRef(null);
  const buscarSugerencias = (idx, texto) => {
    if (sugTimer.current) clearTimeout(sugTimer.current);
    const t = String(texto || "").trim();
    if (t.length < 2) {
      setSugerencias({ idx: null, items: [] });
      return;
    }
    sugTimer.current = setTimeout(async () => {
      try {
        const res = await buscarCuentas(ejercicioId, t, 12);
        // Las cuentas sintéticas de otros asientos (-AJ) no se sugieren: el
        // asiento nuevo debe apuntar a la cuenta base.
        const items = (res.data || []).filter((c) => !esCuentaDeAsiento(c.codigo_full));
        setSugerencias({ idx, items: items.slice(0, 8) });
      } catch {
        setSugerencias({ idx: null, items: [] });
      }
    }, 250);
  };
  const elegirSugerencia = (idx, c) => {
    setFilas((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, codigo_full: c.codigo_full, nombre: c.nombre || "" } : f))
    );
    setSugerencias({ idx: null, items: [] });
  };
  const cerrarSugerencias = () => setSugerencias({ idx: null, items: [] });

  // W19.7: consulta — asientos con el detalle de apuntes expandido
  const [expandidos, setExpandidos] = useState(() => new Set());

  // Borrado con confirmación
  const [aBorrar, setABorrar] = useState(null); // asiento seleccionado
  const [borrando, setBorrando] = useState(false);

  useEffect(() => {
    loadAsientos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ejercicioId]);

  const loadAsientos = async () => {
    try {
      const res = await getAsientos(ejercicioId);
      setAsientos(Array.isArray(res.data) ? res.data : res.data?.asientos || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cargar los asientos");
    } finally {
      setLoading(false);
    }
  };

  // Totales en vivo del formulario
  const { totalCargo, totalAbono, diferencia, cuadra } = useMemo(() => {
    const tc = filas.reduce((acc, f) => acc + parseImporte(f.cargo), 0);
    const ta = filas.reduce((acc, f) => acc + parseImporte(f.abono), 0);
    const diff = tc - ta;
    return {
      totalCargo: tc,
      totalAbono: ta,
      diferencia: diff,
      // Partida doble con tolerancia de 1 céntimo, y que haya importes.
      cuadra: Math.abs(diff) <= 0.01 && (tc > 0 || ta > 0),
    };
  }, [filas]);

  const hayImportes = totalCargo > 0 || totalAbono > 0;
  const conceptoValido = concepto.trim().length > 0;
  const puedeGuardar = conceptoValido && hayImportes && cuadra && !saving;

  const setFila = (idx, campo, valor) => {
    setFilas((prev) => prev.map((f, i) => (i === idx ? { ...f, [campo]: valor } : f)));
  };
  const addFila = () => setFilas((prev) => [...prev, filaVacia()]);
  const removeFila = (idx) =>
    setFilas((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const limpiarFormulario = () => {
    setConcepto("");
    setFecha("");
    setFilas([filaVacia(), filaVacia()]);
    setSinCaja(false);
    setEditandoId(null);
  };

  // W19.7: precarga el formulario con un asiento existente para editarlo.
  const handleEditar = (asiento) => {
    setEditandoId(asiento.id);
    setConcepto(asiento.concepto || "");
    setFecha(asiento.fecha || "");
    setSinCaja(Boolean(asiento.sin_caja));
    setFilas(
      (asiento.apuntes || []).map((a) => ({
        codigo_full: a.codigo_full || "",
        nombre: a.nombre || "",
        cargo: a.cargo ? String(a.cargo) : "",
        abono: a.abono ? String(a.abono) : "",
      }))
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleExpandido = (id) => {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleGuardar = async () => {
    if (!puedeGuardar) return;
    // Solo se envían las filas con código e importe.
    const apuntes = filas
      .filter((f) => f.codigo_full.trim() && (parseImporte(f.cargo) > 0 || parseImporte(f.abono) > 0))
      .map((f) => ({
        codigo_full: f.codigo_full.trim(),
        ...(f.nombre.trim() ? { nombre: f.nombre.trim() } : {}),
        cargo: parseImporte(f.cargo),
        abono: parseImporte(f.abono),
      }));

    if (apuntes.length === 0) {
      toast.error("Añade al menos un apunte con código e importe");
      return;
    }

    const data = {
      concepto: concepto.trim(),
      ...(fecha ? { fecha } : {}),
      apuntes,
      sin_caja: sinCaja,
    };

    setSaving(true);
    try {
      // W19.7: en edición, el PUT revierte los apuntes viejos y aplica los
      // nuevos con el mismo id (los saldos quedan como si el asiento se hubiera
      // creado directamente así).
      const res = editandoId
        ? await actualizarAsiento(ejercicioId, editandoId, data)
        : await crearAsiento(ejercicioId, data);
      toast.success(
        res.data?.message ||
          (editandoId ? "Asiento actualizado" : "Asiento creado correctamente")
      );
      limpiarFormulario();
      await loadAsientos();
      // El asiento modifica los saldos del ejercicio: refresca el resto de la app.
      notifyDataChanged("ejercicios");
    } catch (error) {
      // El backend devuelve 400 con `detail` (p. ej. "no cuadra"). Lo mostramos tal cual.
      toast.error(error.response?.data?.detail || "No se pudo guardar el asiento");
    } finally {
      setSaving(false);
    }
  };

  const handleBorrar = async () => {
    if (!aBorrar) return;
    setBorrando(true);
    try {
      await borrarAsiento(ejercicioId, aBorrar.id);
      toast.success("Asiento borrado. El ajuste ha sido revertido.");
      setABorrar(null);
      await loadAsientos();
      notifyDataChanged("ejercicios");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo borrar el asiento");
    } finally {
      setBorrando(false);
    }
  };

  const goBack = async () => {
    navigate(`/ejercicios/${ejercicioId}/analisis?tab=balance`);
  };

  // Total de un asiento existente = suma de cargos de sus apuntes (= suma de abonos).
  const totalAsiento = (asiento) =>
    (asiento.apuntes || []).reduce((acc, a) => acc + (Number(a.cargo) || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={goBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="page-title">
            Asientos manuales
          </h1>
          <p className="text-muted-foreground mt-1">
            Ajusta el balance con asientos de partida doble (Σ Debe = Σ Haber).
          </p>
        </div>
      </div>

      {/* Formulario de asiento (alta o edición, W19.7) */}
      <Card>
        <CardHeader>
          <CardTitle>
            {editandoId ? "Editar asiento" : "Nuevo asiento"}
          </CardTitle>
          {editandoId && (
            <p className="text-sm text-muted-foreground">
              Al guardar se revierten los apuntes anteriores y se aplican los nuevos
              (los saldos quedan como si el asiento se hubiera creado así).
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Concepto + fecha */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="concepto">
                Concepto <span className="text-rose-600">*</span>
              </Label>
              <Input
                id="concepto"
                value={concepto}
                onChange={(e) => setConcepto(e.target.value)}
                placeholder="Ej.: Reclasificación de deuda a corto plazo"
                data-testid="asiento-concepto"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fecha">Fecha (opcional)</Label>
              <Input
                id="fecha"
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                data-testid="asiento-fecha"
              />
            </div>
          </div>

          {/* P15: sin movimiento de caja */}
          <label className="flex items-start gap-2 text-sm cursor-pointer select-none"
                 data-testid="asiento-sin-caja">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-primary"
              checked={sinCaja}
              onChange={(e) => setSinCaja(e.target.checked)}
            />
            <span>
              <b>Sin movimiento de caja</b>
              <span className="block text-xs text-muted-foreground">
                El asiento modifica el balance (p. ej. activación de rentings:
                inmovilizado contra deuda) pero NO mueve tesorería: el Estado de
                Flujos lo excluye y lo lista como nota. Solo cuentas de balance,
                nunca 57x.
              </span>
            </span>
          </label>

          {/* Tabla de apuntes editable */}
          <div className="space-y-2">
            <Label>Apuntes</Label>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[160px]">Código cuenta</TableHead>
                    <TableHead>Nombre (opcional)</TableHead>
                    <TableHead className="w-[150px] text-right">Debe (cargo)</TableHead>
                    <TableHead className="w-[150px] text-right">Haber (abono)</TableHead>
                    <TableHead className="w-[48px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filas.map((f, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <div className="relative">
                          <Input
                            value={f.codigo_full}
                            onChange={(e) => {
                              setFila(idx, "codigo_full", e.target.value);
                              buscarSugerencias(idx, e.target.value);
                            }}
                            onBlur={() => setTimeout(cerrarSugerencias, 150)}
                            onKeyDown={(e) => e.key === "Escape" && cerrarSugerencias()}
                            placeholder="Ej.: 5720000"
                            className="font-mono"
                            inputMode="numeric"
                            autoComplete="off"
                            data-testid={`apunte-codigo-${idx}`}
                          />
                          {sugerencias.idx === idx && sugerencias.items.length > 0 && (
                            <div className="absolute left-0 top-full z-20 mt-1 w-[320px] max-h-56 overflow-y-auto rounded-md border bg-popover shadow-md">
                              {sugerencias.items.map((c) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-muted/60"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    elegirSugerencia(idx, c);
                                  }}
                                  data-testid={`sugerencia-${c.codigo_full}`}
                                >
                                  <span className="font-mono shrink-0">{c.codigo_full}</span>
                                  <span className="flex-1 truncate text-muted-foreground" title={c.nombre}>
                                    {c.nombre}
                                  </span>
                                  <span className="font-mono tabular-nums text-muted-foreground shrink-0">
                                    {formatCurrency(c.saldo)}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={f.nombre}
                          onChange={(e) => setFila(idx, "nombre", e.target.value)}
                          placeholder="Nombre de la cuenta"
                          data-testid={`apunte-nombre-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={f.cargo}
                          onChange={(e) => setFila(idx, "cargo", e.target.value)}
                          placeholder="0,00"
                          inputMode="decimal"
                          className="text-right font-mono tabular-nums"
                          data-testid={`apunte-cargo-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={f.abono}
                          onChange={(e) => setFila(idx, "abono", e.target.value)}
                          placeholder="0,00"
                          inputMode="decimal"
                          className="text-right font-mono tabular-nums"
                          data-testid={`apunte-abono-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFila(idx)}
                          disabled={filas.length <= 1}
                          aria-label="Quitar fila"
                          data-testid={`apunte-remove-${idx}`}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Button variant="outline" size="sm" onClick={addFila} data-testid="apunte-add">
              <Plus className="h-4 w-4 mr-2" />
              Añadir fila
            </Button>
          </div>

          {/* Indicador en vivo de cuadre */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 rounded-lg border bg-muted/40 px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">Total Debe</span>
                <p className="font-mono font-semibold tabular-nums" data-testid="total-debe">
                  {formatCurrency(totalCargo)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Total Haber</span>
                <p className="font-mono font-semibold tabular-nums" data-testid="total-haber">
                  {formatCurrency(totalAbono)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Diferencia</span>
                <p
                  className={`font-mono font-semibold tabular-nums ${
                    cuadra ? "text-emerald-600" : "text-rose-600"
                  }`}
                  data-testid="diferencia"
                >
                  {formatCurrency(diferencia)}
                </p>
              </div>
            </div>
            <div>
              {cuadra ? (
                <span
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                  data-testid="estado-cuadre"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Cuadra
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-2 rounded-full bg-rose-100 px-3 py-1 text-sm font-semibold text-rose-700 dark:bg-rose-950/40 dark:text-rose-400"
                  data-testid="estado-cuadre"
                >
                  <XCircle className="h-4 w-4" />
                  No cuadra
                </span>
              )}
            </div>
          </div>

          {/* Acciones */}
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={limpiarFormulario} disabled={saving}>
              {editandoId ? "Cancelar edición" : "Limpiar"}
            </Button>
            <Button onClick={handleGuardar} disabled={!puedeGuardar} data-testid="asiento-guardar">
              {saving
                ? "Guardando..."
                : editandoId
                ? "Guardar cambios"
                : "Guardar asiento"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de asientos existentes */}
      <Card>
        <CardHeader>
          <CardTitle>Asientos registrados</CardTitle>
          <p className="text-sm text-muted-foreground flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            Borrar un asiento revierte el ajuste: los saldos de las cuentas afectadas vuelven a su
            estado anterior.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Concepto</TableHead>
                <TableHead className="w-[130px]">Fecha</TableHead>
                <TableHead className="w-[110px] text-center">Nº apuntes</TableHead>
                <TableHead className="w-[150px] text-right">Total</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : asientos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Todavía no hay asientos manuales en este ejercicio
                  </TableCell>
                </TableRow>
              ) : (
                asientos.map((asiento) => {
                  const abierto = expandidos.has(asiento.id);
                  return (
                    <React.Fragment key={asiento.id}>
                      <TableRow className="hover:bg-muted/50">
                        <TableCell className="font-medium max-w-[360px]">
                          {/* W19.7 (consultar): expandir para ver los apuntes */}
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-left hover:text-primary"
                            onClick={() => toggleExpandido(asiento.id)}
                            data-testid={`asiento-expandir-${asiento.id}`}
                          >
                            {abierto ? (
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                            <span className="truncate">{asiento.concepto}</span>
                            {asiento.sin_caja && (
                              <span className="shrink-0 rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300"
                                    title="Sin movimiento de caja: excluido del Estado de Flujos">
                                sin caja
                              </span>
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {asiento.fecha || "—"}
                        </TableCell>
                        <TableCell className="text-center tabular-nums">
                          {(asiento.apuntes || []).length}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {formatCurrency(totalAsiento(asiento))}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditar(asiento)}
                            aria-label="Editar asiento"
                            data-testid={`asiento-editar-${asiento.id}`}
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setABorrar(asiento)}
                            aria-label="Borrar asiento"
                            data-testid={`asiento-borrar-${asiento.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {abierto && (
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell colSpan={5} className="py-2">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="h-8 text-xs w-[140px]">Código</TableHead>
                                  <TableHead className="h-8 text-xs">Nombre</TableHead>
                                  <TableHead className="h-8 text-xs text-right w-[140px]">Debe</TableHead>
                                  <TableHead className="h-8 text-xs text-right w-[140px]">Haber</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {(asiento.apuntes || []).map((a, i) => (
                                  <TableRow key={i} className="hover:bg-transparent">
                                    <TableCell className="py-1.5 text-xs font-mono">
                                      {a.codigo_full}
                                    </TableCell>
                                    <TableCell className="py-1.5 text-xs">
                                      {a.nombre || "—"}
                                    </TableCell>
                                    <TableCell className="py-1.5 text-xs text-right font-mono tabular-nums">
                                      {Number(a.cargo) > 0 ? formatCurrency(a.cargo) : "—"}
                                    </TableCell>
                                    <TableCell className="py-1.5 text-xs text-right font-mono tabular-nums">
                                      {Number(a.abono) > 0 ? formatCurrency(a.abono) : "—"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Confirmación de borrado */}
      <AlertDialog open={!!aBorrar} onOpenChange={(o) => !o && setABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar este asiento?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el asiento
              {aBorrar?.concepto ? ` "${aBorrar.concepto}"` : ""} y se revertirá el ajuste sobre los
              saldos de las cuentas afectadas. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={borrando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleBorrar();
              }}
              disabled={borrando}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirmar-borrar-asiento"
            >
              {borrando ? "Borrando..." : "Borrar asiento"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
